import { useMemo } from 'react';

import { parseLocalDate } from '@/lib/utils';

interface CountdownResult {
  days: number;
  hours: number;
  isToday: boolean;
  isPast: boolean;
}

export function useCountdown(
  targetDate: string | null,
): CountdownResult | null {
  return useMemo(() => {
    if (!targetDate) {
      return null;
    }

    const now = new Date();
    const target = parseLocalDate(targetDate);

    // Set both dates to midnight for day comparison
    const nowMidnight = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate(),
    );

    // Create this year's occurrence of the date
    let thisYearDate = new Date(
      now.getFullYear(),
      target.getMonth(),
      target.getDate(),
    );

    // If the date has already passed this year, use next year's occurrence
    if (thisYearDate < nowMidnight) {
      thisYearDate = new Date(
        now.getFullYear() + 1,
        target.getMonth(),
        target.getDate(),
      );
    }

    const diffTime = thisYearDate.getTime() - nowMidnight.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    const isToday = diffDays === 0;
    const isPast = diffDays < 0;

    // Calculate hours remaining in the current day
    const hoursRemaining = 24 - now.getHours();

    return {
      days: diffDays,
      hours: hoursRemaining,
      isToday,
      isPast,
    };
  }, [targetDate]);
}
